import React from 'react';
import { Container, Typography, Button, Grid, Box, Card, CardContent, CardActions, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Package, Layers, PlusCircle, TrendingUp,
  FileText, Users, BarChart3, Truck, Globe, FileDown, ArrowLeftRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, error } = useAuth();

  const menuItems = [
    { label: "View Orders", path: "/orders", icon: ShoppingCart, color: "#1976d2", roles: ["owner", "employee"] },
    { label: "Manage Products", path: "/manage-products", icon: Package, color: "#9c27b0", roles: ["owner", "employee"] },
    { label: "Manage Categories", path: "/categories", icon: Layers, color: "#0288d1", roles: ["owner"] },
    { label: "Create Order", path: "/create-order", icon: PlusCircle, color: "#2e7d32", roles: ["owner", "employee"] },
    { label: "Stock Addition", path: "/purchase-orders", icon: TrendingUp, color: "#ed6c02", roles: ["owner", "employee"] },
    { label: "Stock Transfer", path: "/stock-transfers", icon: ArrowLeftRight, color: "#0288d1", roles: ["owner", "employee"] },
    { label: "Stock Movement Report", path: "/sales-report", icon: FileText, color: "#f57c00", roles: ["owner", "employee"] },
    { label: "View Parties", path: "/view-parties", icon: Users, color: "#616161", roles: ["owner", "employee"] },
    { label: "Generate Reports", path: "/reports", icon: FileDown, color: "#5e35b1", roles: ["owner"] },
    { label: "Registered Users", path: "/registered-users", icon: Users, color: "#d32f2f", roles: ["owner"] },
    { label: "View Statistics", path: "/statistics", icon: BarChart3, color: "#c62828", roles: ["owner"] },
    { label: "Assigned Deliveries", path: "/assigned-deliveries", icon: Truck, color: "#1976d2", roles: ["delivery_person"] }
  ];

  const availableItems = menuItems.filter(item => user && item.roles.includes(user.role));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <Header showProfileButton={true} isDashboard={true} />
      <Container sx={{ flexGrow: 1, py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Welcome back, {user?.name || 'User'}!
            </Typography>
            {error && <Typography color="error">Error: {error}</Typography>}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Globe size={20} />}
            onClick={() => navigate('/')}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderWidth: 2,
                bgcolor: 'primary.main',
                color: 'white'
              }
            }}
          >
            Visit Website
          </Button>
        </Box>

        <Grid container spacing={3}>
          {availableItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
                    color: 'white',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${alpha(item.color, 0.4)}`,
                    },
                  }}
                  onClick={() => navigate(item.path)}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={32} />
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                      {getDescription(item.label)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                    <Button
                      size="small"
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        },
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      Go to {item.label.split(' ')[0]}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

const getDescription = (label) => {
  const descriptions = {
    "View Orders": "Track and manage all customer orders",
    "Manage Products": "Add, edit, and organize products",
    "Manage Categories": "Organize product categories",
    "Create Order": "Place new customer orders",
    "Stock Addition": "Record incoming inventory",
    "Stock Transfer": "Move stock between locations",
    "Stock Movement Report": "View inventory movements",
    "View Parties": "Manage customer information",
    "Registered Users": "Manage system users",
    "View Statistics": "Analyze business metrics",
    "Assigned Deliveries": "View your delivery tasks"
  };
  return descriptions[label] || "Manage your inventory";
};

export default Dashboard;